import React, { FunctionComponent, useState } from 'react'
import { Button, ButtonToolbar, Form, Modal, Spinner } from 'react-bootstrap'
import { useWorkroom } from './WorkroomContext'
import { createNewFile } from 'Frontend/scripts/services/workrooms.service'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface SaveFileForm {
  file: FileList
  description: string
}

const WorkroomCreateFileButton: FunctionComponent = () => {
  const { permissions, currentWorkroomId, fetchFilesAndFolders, currentFolder, setIsLoading, isLoading } = useWorkroom()

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [formFile, setFormFile] = useState<File | null>(null)
  const { t } = useTranslation()
  const form = useForm<SaveFileForm>()

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = form

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFormFile(e.target.files[0])
    }
  }

  const onSubmit = () => {
    setIsLoading(true)
    if (!permissions?.createWorkroomDocument || !formFile) return

    createNewFile(
      currentWorkroomId as string,
      currentFolder !== 'root' ? currentFolder : null,
      formFile.name,
      formFile,
    ).then(() => {
      reset()
      setIsOpen(!isOpen)
      fetchFilesAndFolders()
      setIsLoading(false)
    })
  }

  const handleClose = () => {
    reset()
    setIsOpen(!isOpen)
  }

  return (
    <>
      <Button
        variant="secondary"
        className="fw-normal"
        disabled={!permissions?.createWorkroomDocument}
        onClick={() => setIsOpen(!isOpen)}
      >
        <i className={`fa fa-file-o small me-1`} />
        {t('workrooms.documents.new-file')}
      </Button>
      <Modal show={isOpen} centered>
        <Modal.Header className="h4 mb-0">{t('workrooms.documents.new-file-modal-title')}</Modal.Header>
        <Modal.Body>
          {isLoading && (
            <div
              className="d-flex top-0 start-0 bottom-0 end-0 h-100 
            justify-content-center position-absolute align-items-center"
            >
              <Spinner variant="secondary" animation="border" />
            </div>
          )}
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Group>
              <Form.Label>{t('workrooms.documents.new-file-form-file-label')}</Form.Label>
              <Form.Control
                {...register('file', {
                  required: { message: t('workrooms.documents.new-file-form-required-file-validation'), value: true },
                  validate: {
                    fileType: (file: FileList) => {
                      const allowedTypes = [
                        'application/pdf',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'application/msword',
                        'application/vnd.ms-powerpoint',
                        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                        'text/plain',
                        'image/jpeg',
                        'image/jpg',
                        'image/png',
                        'image/gif',
                      ]
                      return file && allowedTypes.includes(file[0].type)
                        ? true
                        : t('workrooms.documents.new-file-form-file-typ-validation')
                    },
                    fileSize: (file: FileList) => {
                      const maxSizeInMB = 10
                      return file && file[0].size <= maxSizeInMB * 1024 * 1024
                        ? true
                        : `${t('workrooms.documents.new-file-form-file-size-validation')} ${maxSizeInMB} ${t('workrooms.documents.mb')}`
                    },
                  },
                })}
                type="file"
                disabled={isLoading}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e)}
                placeholder={t('workrooms.documents.new-file-form-file-placeholder')}
                accept=".pdf, .xlsx, .xls, .docx, .doc, .txt, .jpg, .jpeg, .png and .gif, .ppt, .pptx"
                className={`${errors?.file ? 'is-invalid' : ''}`}
              />
              {errors?.file && <p className="invalid-feedback">{errors?.file.message}</p>}
            </Form.Group>
            <ButtonToolbar className="justify-content-between mt-3">
              <Button type="submit" variant="secondary" disabled={isLoading}>
                {t('save')}
              </Button>
              <Button type="button" variant="outline-secondary" onClick={() => handleClose()} disabled={isLoading}>
                {t('abort')}
              </Button>
            </ButtonToolbar>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  )
}
export default WorkroomCreateFileButton
